/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState, useCallback} from 'react'
import {Dialog, DialogPanel, DialogTitle} from '@headlessui/react'
import {useTranslation} from 'react-i18next'
import {useLocation, useNavigate} from 'react-router-dom'
import type {Offer} from 'types/entities/Offer'
import type {OfferCalculation} from 'types/entities/OfferCalculation'
import UiButton, {UiButtonSize, UiButtonStyle} from 'components/Ui/UiButton'
import {useAppDispatch} from 'helpers/store'
import {ModalSize} from 'types/enums/ModalSize'
import CreateOfferCalculationModal from 'components/Offers/CreateOfferCalculationModal'
import BookingSellerDuplicateOfferModal from 'pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerDuplicateOfferModal'
import {OffersAction} from 'store/offers/OffersAction'
import {toast} from 'utils/toastUtils'
import ReservationCheckoutPlannerModal from 'components/ReservationCheckoutPlanner/ReservationCheckoutPlannerModal'
import {Tooltip} from 'react-tooltip'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'

import {useOptionalOfferDetailContext} from 'pages/Front/Offers/components/OfferDetailContext'
import {resolveOfferReturnTo, getOfferEditLockedTooltip} from 'utils/offers/offersUtils'
import BookingSellerPaymentTypeModal from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerPaymentTypeModal";
import {exportOfferConfirmationPdf} from 'utils/offers/offerPdfExport'
import {OfferPdfDownloadError} from 'utils/offers/offerPdfDownload'
import { capturePosthogEvent } from 'utils/posthogUtils'
import { OfferStatus } from 'types/enums/OfferStatus'

interface OfferDetailActionsProps {
    selectedOffer?: Offer
    targetCalculation?: OfferCalculation
    isExportingPdf?: boolean
    setIsExportingPdf?: (val: boolean) => void
    onClose?: () => void
}

const OfferDetailActions: React.FC<OfferDetailActionsProps> = ({
    selectedOffer: selectedOfferProp,
    targetCalculation: targetCalculationProp,
    isExportingPdf: isExportingPdfProp,
    setIsExportingPdf: setIsExportingPdfProp,
    onClose: onCloseProp,
}) => {
    const { t } = useTranslation()
    const offerDetailContext = useOptionalOfferDetailContext()
    const selectedOffer = selectedOfferProp ?? offerDetailContext?.selectedOffer
    const targetCalculation = targetCalculationProp ?? offerDetailContext?.targetCalculation
    const isExportingPdf = isExportingPdfProp ?? offerDetailContext?.isExportingPdf ?? false
    const setIsExportingPdf = setIsExportingPdfProp ?? offerDetailContext?.setIsExportingPdf ?? (() => {})
    const onClose = onCloseProp ?? offerDetailContext?.onClose
    const [showPaymentType, setShowPaymentType] = useState(false)
    const dispatch = useAppDispatch()
    const [actionModal, setActionModal] = useState<{content: React.ReactNode; title?: string; wide?: boolean} | null>(null)
    const closeModal = useCallback(() => setActionModal(null), [])
    const openModal = (content: React.ReactNode, options: {modalSize?: ModalSize; closeModal?: () => void}, title?: string) => {
        setActionModal({content, title, wide: options.modalSize === ModalSize.RoundedFullscreen})
    }
    const navigate = useNavigate()
    const location = useLocation()

    const returnTo = resolveOfferReturnTo(location.state as { returnTo?: string } | null)

    if (!selectedOffer) {
        return null
    }

    const isOfferRejected = selectedOffer.status === OfferStatus.Rejected
    const editLockedTooltip = getOfferEditLockedTooltip(t, selectedOffer.status)

    const onDuplicateHandler = () => {
        capturePosthogEvent('offer_duplicated', { offer_id: selectedOffer.id })
        openModal(
            <BookingSellerDuplicateOfferModal
                offer_id={selectedOffer.id}
                title={t('Duplicate offer')}
                closeModal={closeModal}
            />,
            {
                modalSize: ModalSize.Small,
                closeModal,
            },
            t('Duplicate Offer')
        )
    }

    const onPaymentTypeHandler = () => {
        if (!isOfferRejected) setShowPaymentType(true)
    }

    const openCheckoutPlannerModal = () => {
        capturePosthogEvent('checkout_planner_opened', { offer_id: selectedOffer.id })
        openModal(
            <ReservationCheckoutPlannerModal
                offerId={selectedOffer.id}
                initialPlans={selectedOffer.extra?.reservation_checkout_plan ?? []}
                initialInvoiceNote={selectedOffer.extra?.invoice_note}
                initialOfferNote={selectedOffer.extra?.offer_note}
            />,
            {
                modalSize: ModalSize.Medium,
                closeModal,
            },
            t('Checkout Planner')
        )
    }

    const onCheckoutPlannerHandler = () => {
        if (isOfferRejected) return
        openCheckoutPlannerModal()
    }

    const onDeleteHandler = () => {
        if (isOfferRejected) return
        openModal(<UiAlertConfirmationModal
            title={ t('Delete Offer')}
            message={t('Are you sure you want to delete this offer?')}
            onCancel={closeModal}
            onConfirm={async () => {
                try {
                    await dispatch(OffersAction.fetchDeleteOffer(selectedOffer.id))
                    capturePosthogEvent('offer_deleted', { offer_id: selectedOffer.id })
                    toast.success(t('Offer deleted'))
                    if (onClose) {
                        onClose()
                    } else {
                        navigate(returnTo)
                    }
                    closeModal()
                } catch (e) {
                    console.error(e)
                    toast.error(t('Failed to delete offer'))
                    closeModal()
                }
            }}
        />, {modalSize: ModalSize.Small}, t('Confirm'))
    }

    const onExportConfirmationPdfHandler = async () => {
        if (!targetCalculation?.id) {
            toast.error(t('No calculation available to create booking'))
            return
        }

        setIsExportingPdf(true)
        try {
            const exported = await exportOfferConfirmationPdf({
                dispatch,
                exportCalculationId: targetCalculation.id,
                offerNumber: selectedOffer.number,
                calculationName: targetCalculation.name,
                fallbackCalculationIndex: 1,
            })

            if (exported) {
                capturePosthogEvent('offer_pdf_exported', {
                    offer_id: selectedOffer.id,
                    calculation_id: targetCalculation.id,
                })
            } else {
                toast.error(t('Export failed'))
            }
        } catch (error) {
            console.error(error)
            toast.error(t(error instanceof OfferPdfDownloadError ? error.messageKey : 'Export failed'))
        } finally {
            setIsExportingPdf(false)
        }
    }

    return (
        <div className="OfferDetailActions UiCard p-3 flex flex-col gap-2">
            <div className="flex flex-col gap-2">
                <UiButton
                    title={t('New Calculation')}
                    size={UiButtonSize.Small}
                    style={isOfferRejected ? UiButtonStyle.Gray : UiButtonStyle.Primary}
                    disabled={isOfferRejected}
                    dataTooltipId={isOfferRejected ? 'offer-detail-actions-disabled-tooltip' : undefined}
                    dataTooltipContent={isOfferRejected ? editLockedTooltip : undefined}
                    onClick={() => {
                        if (isOfferRejected) return
                        openModal(
                            <CreateOfferCalculationModal
                                offer_id={selectedOffer.id}
                                is_secondary={true}
                                onClose={closeModal}
                                onCreated={() => {
                                    capturePosthogEvent('offer_calculation_created', { offer_id: selectedOffer.id })
                                    dispatch(OffersAction.fetchOfferById(selectedOffer.id))
                                }}
                            />,
                            {
                                modalSize: ModalSize.RoundedFullscreen,
                                closeModal,
                            },
                            t('New Calculation')
                        )
                    }}
                    className="h-9 text-xs"
                />
                <UiButton
                    title={t('Duplicate Offer')}
                    size={UiButtonSize.Small}
                    style={UiButtonStyle.Primary}
                    onClick={onDuplicateHandler}
                    className="h-9 text-xs"
                />
				<UiButton
                    title={t('Export Confirmation PDF')}
                    size={UiButtonSize.Small}
                    style={UiButtonStyle.Primary}
                    onClick={onExportConfirmationPdfHandler}
                    disabled={isExportingPdf || !targetCalculation?.id}
                    className="h-9 text-xs"
                />
				<UiButton
                    title={t('Payment Type')}
                    size={UiButtonSize.Small}
					style={isOfferRejected ? UiButtonStyle.Gray : UiButtonStyle.Primary}
                    disabled={isOfferRejected}
                    dataTooltipId={isOfferRejected ? 'offer-detail-actions-disabled-tooltip' : undefined}
                    dataTooltipContent={isOfferRejected ? editLockedTooltip : undefined}
                    onClick={onPaymentTypeHandler}
                    className="h-9 text-xs"
                />
                <UiButton
                    title={t('Checkout Planner')}
                    size={UiButtonSize.Small}
                    style={isOfferRejected ? UiButtonStyle.Gray : UiButtonStyle.Primary}
                    disabled={isOfferRejected}
                    dataTooltipId={isOfferRejected ? 'offer-detail-actions-disabled-tooltip' : undefined}
                    dataTooltipContent={isOfferRejected ? editLockedTooltip : undefined}
                    onClick={onCheckoutPlannerHandler}
                    className="h-9 text-xs"
                />
                <UiButton
                    title={t('Delete Offer')}
                    size={UiButtonSize.Small}
                    style={isOfferRejected ? UiButtonStyle.Gray : UiButtonStyle.Red}
                    disabled={isOfferRejected}
                    dataTooltipId={isOfferRejected ? 'offer-detail-actions-disabled-tooltip' : undefined}
                    dataTooltipContent={isOfferRejected ? editLockedTooltip : undefined}
                    onClick={onDeleteHandler}
                    className="h-9 text-xs"
                />
            </div>
            <Dialog open={!!actionModal} onClose={closeModal} className="relative z-[1400]">
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <DialogPanel className={actionModal?.wide ? 'w-full max-w-7xl max-h-[90vh] overflow-auto rounded-lg bg-white dark:bg-gray-950 p-4' : 'w-full max-w-3xl max-h-[80vh] overflow-auto rounded-lg bg-white dark:bg-gray-950 p-4'}>
                        <div className="flex items-center justify-between">
                            <DialogTitle>{actionModal?.title}</DialogTitle>
                            <UiButton title={t('Close')} onClick={closeModal} />
                        </div>
                        {actionModal?.content}
                    </DialogPanel>
                </div>
            </Dialog>
            <Dialog open={showPaymentType} onClose={() => setShowPaymentType(false)} className="relative z-[1400]">
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <DialogPanel className="w-full max-w-xl max-h-[80vh] overflow-auto rounded-lg bg-white dark:bg-gray-950 p-4">
                        <div className="flex items-center justify-between">
                            <DialogTitle>{t('Payment Type')}</DialogTitle>
                            <UiButton title={t('Close')} onClick={() => setShowPaymentType(false)} />
                        </div>
                        <BookingSellerPaymentTypeModal offer_id={selectedOffer.id} closeModal={() => setShowPaymentType(false)} />
                    </DialogPanel>
                </div>
            </Dialog>
            <Tooltip id="calculation-item-action-tooltip" style={{ zIndex: 99999 }} />
            <Tooltip id="offer-detail-actions-disabled-tooltip" style={{ zIndex: 99999 }} />

        </div>
    )
}

export default OfferDetailActions
